import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import BarcodeCameraModal from "@/components/BarcodeCameraModal";
import { scanCache, isNetworkError } from "@/lib/scanCache";
import { routeScan } from "@/lib/qrRouting";

/**
 * Processed Food Scanner (Pro-only)
 *
 * Three input methods (any combination):
 *   • Front-of-pack photo   → AI reads brand + product name + marketing claims
 *   • Back-of-pack photo    → AI reads ingredients list + nutrition per 100 g
 *   • Barcode number        → Open Food Facts lookup
 *
 * The backend synthesises everything into a structured verdict.
 */
export default function FoodScanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [frontImg, setFrontImg] = useState(null); // { preview, b64 }
  const [backImg, setBackImg] = useState(null);
  const [barcode, setBarcode] = useState("");
  const [supermarketUrl, setSupermarketUrl] = useState("");
  const [analysing, setAnalysing] = useState(false);
  const [result, setResult] = useState(null);
  const [favourites, setFavourites] = useState([]);
  const [savingFav, setSavingFav] = useState(false);
  const [remainingFree, setRemainingFree] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [allergenCatalog, setAllergenCatalog] = useState([]);
  const [myAllergens, setMyAllergens] = useState([]);
  const [allergenPickerOpen, setAllergenPickerOpen] = useState(false);
  const [nastiesLibrary, setNastiesLibrary] = useState([]);
  const [myNasties, setMyNasties] = useState([]);
  const [nastiesPickerOpen, setNastiesPickerOpen] = useState(false);
  const [offlineHit, setOfflineHit] = useState(null); // { entry, days_old }
  const [savedScans, setSavedScans] = useState(() => scanCache.list());
  const [savedOpen, setSavedOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(() => scanCache.queueCount());
  const [isOnline, setIsOnline] = useState(() => (typeof navigator !== "undefined" ? navigator.onLine : true));
  const [urlChoice, setUrlChoice] = useState(null); // { url } for QR → unknown URL prompt
  const frontRef = useRef(null);
  const backRef = useRef(null);

  const isPro = !!user?.is_pro;

  useEffect(() => {
    // Everyone — Pro or Free — gets allergen safety checks.
    (async () => {
      try {
        const { data } = await api.get("/food-scan/allergens");
        setAllergenCatalog(data.catalog || []);
        setMyAllergens(data.allergens || []);
      } catch {
        /* silent */
      }
      try {
        const { data } = await api.get("/food-scan/nasties");
        setNastiesLibrary(data.library || []);
        setMyNasties(data.nasties || []);
      } catch {
        /* silent */
      }
    })();

    if (!isPro) return;
    (async () => {
      try {
        const { data } = await api.get("/food-scan/favourites");
        setFavourites(data.favourites || []);
      } catch {
        /* silent */
      }
    })();
  }, [isPro]);

  const toggleAllergen = async (key) => {
    const next = myAllergens.includes(key)
      ? myAllergens.filter((k) => k !== key)
      : [...myAllergens, key];
    setMyAllergens(next); // optimistic
    try {
      await api.put("/food-scan/allergens", { allergens: next });
    } catch {
      toast.error("Couldn't save your preferences — try again.");
      setMyAllergens(myAllergens); // rollback
    }
  };

  const saveNasties = async (next) => {
    const prev = myNasties;
    setMyNasties(next); // optimistic
    try {
      const { data } = await api.put("/food-scan/nasties", { nasties: next });
      setMyNasties(data.nasties || next);
    } catch {
      toast.error("Couldn't save your watchlist — try again.");
      setMyNasties(prev);
    }
  };

  const toggleNasty = (label) => {
    const next = myNasties.includes(label)
      ? myNasties.filter((n) => n !== label)
      : [...myNasties, label];
    saveNasties(next);
  };

  const addCustomNasty = (label) => {
    const term = (label || "").trim().slice(0, 40);
    if (!term) return;
    if (myNasties.some((n) => n.toLowerCase() === term.toLowerCase())) return;
    saveNasties([...myNasties, term]);
  };

  const readFile = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const onFrontFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const b64 = await readFile(file);
    setFrontImg({ preview: b64, b64 });
  };
  const onBackFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const b64 = await readFile(file);
    setBackImg({ preview: b64, b64 });
  };

  const clearAll = () => {
    setFrontImg(null);
    setBackImg(null);
    setBarcode("");
    setSupermarketUrl("");
    setResult(null);
    setOfflineHit(null);
  };

  const runScan = useCallback(async (payload) => {
    const { data } = await api.post("/food-scan/analyze", payload);
    const entry = scanCache.save(data.scan, { barcode: payload.barcode });
    setSavedScans(scanCache.list());
    return { scan: data.scan, remaining_free_scans: data.remaining_free_scans, entry };
  }, []);

  const analyse = async () => {
    if (!frontImg && !backImg && !barcode.trim() && !supermarketUrl.trim()) {
      toast.error("Add at least one: front photo, back photo, barcode, or supermarket URL.");
      return;
    }
    setAnalysing(true);
    setResult(null);
    setOfflineHit(null);
    const payload = {
      front_image_base64: frontImg?.b64 || null,
      back_image_base64: backImg?.b64 || null,
      barcode: barcode.trim() || null,
      supermarket_url: supermarketUrl.trim() || null,
    };
    try {
      const { scan, remaining_free_scans } = await runScan(payload);
      setResult(scan);
      if (typeof remaining_free_scans === "number") setRemainingFree(remaining_free_scans);
    } catch (err) {
      // Offline fallback: if the user is on a weak signal and we have a barcode
      // they've already scanned, surface the saved verdict so they can still
      // decide at the shelf.
      if (isNetworkError(err) && payload.barcode) {
        const cached = scanCache.getByBarcode(payload.barcode);
        if (cached) {
          setResult(cached.scan);
          const days = Math.max(0, Math.round((Date.now() - new Date(cached.saved_at).getTime()) / 86400000));
          setOfflineHit({ entry: cached, days_old: days });
          toast.info("No signal — showing your saved result for this barcode.");
          return;
        }
      }
      // Offline + no cache hit? Queue the request so we process it on reconnect.
      if (isNetworkError(err)) {
        scanCache.enqueue(payload);
        setPendingCount(scanCache.queueCount());
        toast.info("Saved — we'll finish this scan the moment you're back online.");
        return;
      }
      const detail = err?.response?.data?.detail;
      if (detail?.code === "pro_only_food_scan_weekly_cap") {
        toast.error(detail.message);
        navigate("/app/billing");
        return;
      }
      if (detail?.code === "pro_only_food_scan") {
        toast.error(detail.message);
        navigate("/app/billing");
        return;
      }
      toast.error(typeof detail === "string" ? detail : detail?.message || "Scan failed.");
    } finally {
      setAnalysing(false);
    }
  };

  // Flush any queued offline scans whenever the browser comes back online.
  const flushQueue = useCallback(async () => {
    const queue = scanCache.queue();
    if (!queue.length) return;
    let okCount = 0;
    for (const item of queue) {
      try {
        await runScan(item.payload);
        scanCache.dequeue(item.id);
        okCount += 1;
      } catch {
        // Still failing — leave the item in the queue for next time.
        break;
      }
    }
    setPendingCount(scanCache.queueCount());
    if (okCount) toast.success(`${okCount} queued scan${okCount === 1 ? "" : "s"} finished — tap "Saved scans" to see them.`);
  }, [runScan]);

  useEffect(() => {
    const onOnline = () => { setIsOnline(true); flushQueue(); };
    const onOffline = () => setIsOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    if (navigator.onLine && scanCache.queueCount() > 0) flushQueue();
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [flushQueue]);

  const saveFavourite = async () => {
    if (!result) return;
    setSavingFav(true);
    try {
      const { data } = await api.post("/food-scan/favourites", {
        product_name: result.product_name || "Unknown product",
        brand: result.brand || null,
        verdict: result.verdict || "moderate",
        nova_level: result.nova_level || null,
        kcal_per_100g: result.nutrition_per_100g?.kcal ?? null,
        sugar_g_per_100g: result.nutrition_per_100g?.sugar_g ?? null,
        salt_g_per_100g: result.nutrition_per_100g?.salt_g ?? null,
        barcode: result.barcode || null,
        summary: result.verdict_reason || null,
        category: result.category || null,
      });
      setFavourites((f) => [data.favourite, ...f]);
      toast.success(`Saved to ${data.favourite.category || "Other"}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "Could not save");
    } finally {
      setSavingFav(false);
    }
  };

  const removeFavourite = async (favourite_id) => {
    try {
      await api.delete(`/food-scan/favourites/${favourite_id}`);
      setFavourites((f) => f.filter((x) => x.favourite_id !== favourite_id));
      toast.success("Removed");
    } catch {
      toast.error("Could not remove");
    }
  };

  if (!isPro) {
    // Free tier gets the scanner but with a "1 free scan/week" freemium teaser
    // and no favourites (Pro-only). We still render the whole flow.
  }

  const freeQuotaHint = isPro
    ? null
    : (remainingFree != null
        ? `${remainingFree} free scan${remainingFree === 1 ? "" : "s"} left this week`
        : "1 free scan per week — Pro is unlimited");

  return (
    <div className="max-w-5xl mx-auto" data-testid="food-scanner-page">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-wide text-[#8B9690]">Is it healthy?</div>
        <h1 className="text-3xl sm:text-4xl font-semibold mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
          Food Scanner
        </h1>
        <p className="text-sm text-[#5C6B61] mt-1 max-w-xl">
          Snap the front, snap the back, or type the barcode — we&apos;ll tell you exactly what&apos;s inside, what it may cause, and whether it&apos;s worth eating.
        </p>
        {freeQuotaHint && (
          <div
            className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#F3F0EA] border border-[#E5E2DA] px-3 py-1.5 text-xs text-[#5C6B61]"
            data-testid="free-quota-hint"
          >
            <span>🎁</span> {freeQuotaHint} · <button onClick={() => navigate("/app/billing")} className="text-[#3B5A3E] font-medium underline">Go Pro</button>
          </div>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            onClick={() => setSavedOpen(true)}
            data-testid="open-saved-scans-btn"
            className="inline-flex items-center gap-2 rounded-full bg-white border border-[#E5E2DA] hover:border-[#3B5A3E] px-3 py-1.5 text-xs text-[#3B5A3E] font-medium transition"
          >
            <span>📚</span> Saved scans
            {savedScans.length > 0 && (
              <span className="rounded-full bg-[#EDF3E9] px-1.5 text-[10px]" data-testid="saved-count">{savedScans.length}</span>
            )}
          </button>
          {!isOnline && (
            <span
              className="inline-flex items-center gap-1.5 rounded-full bg-[#FDE8D7] border border-[#F6C7BE] text-[#7A2317] px-3 py-1.5 text-[11px] font-medium"
              data-testid="offline-pill"
            >
              <span className="w-2 h-2 rounded-full bg-[#A33D26]" /> Offline — rescans still work
            </span>
          )}
          {pendingCount > 0 && (
            <span
              className="inline-flex items-center gap-1.5 rounded-full bg-[#FBE9C4] text-[#7A5A11] px-3 py-1.5 text-[11px] font-medium"
              data-testid="pending-pill"
            >
              <span>⏳</span> {pendingCount} queued — finishes when back online
            </span>
          )}
        </div>
      </div>

      {/* ALLERGEN / "CAN'T EAT" PICKER — persists to user profile so every scan safety-checks it */}
      <AllergenPicker
        catalog={allergenCatalog}
        selected={myAllergens}
        onToggle={toggleAllergen}
        open={allergenPickerOpen}
        setOpen={setAllergenPickerOpen}
      />

      {/* NASTIES WATCHLIST — personal list of chemicals / additives to flag */}
      <NastiesPicker
        library={nastiesLibrary}
        selected={myNasties}
        onToggle={toggleNasty}
        onAddCustom={addCustomNasty}
        open={nastiesPickerOpen}
        setOpen={setNastiesPickerOpen}
      />

      {/* INPUT PANEL */}
      <div className="grid md:grid-cols-3 gap-4">
        <ImagePicker
          testid="front-image-input"
          label="Front of packet"
          hint="Brand + product name"
          value={frontImg}
          onFile={onFrontFile}
          onClear={() => setFrontImg(null)}
          inputRef={frontRef}
        />
        <ImagePicker
          testid="back-image-input"
          label="Back of packet"
          hint="Ingredients + nutrition"
          value={backImg}
          onFile={onBackFile}
          onClear={() => setBackImg(null)}
          inputRef={backRef}
        />
        <div className="rounded-3xl border border-[#E5E2DA] bg-white p-4 flex flex-col">
          <div className="text-xs uppercase tracking-wide text-[#8B9690]">Barcode</div>
          <div className="text-sm text-[#5C6B61] mb-3">Scan with camera or type</div>
          <div className="flex gap-2">
            <input
              type="text"
              inputMode="numeric"
              placeholder="e.g. 5000112637922"
              value={barcode}
              onChange={(e) => setBarcode(e.target.value.replace(/[^0-9]/g, ""))}
              data-testid="barcode-input"
              className="ff-input text-base tracking-widest flex-1 min-w-0"
              maxLength={20}
            />
            <button
              type="button"
              onClick={() => setCameraOpen(true)}
              data-testid="open-camera-scanner-btn"
              aria-label="Scan barcode with camera"
              title="Scan barcode with camera"
              className="shrink-0 rounded-2xl bg-[#3B5A3E] hover:bg-[#2A4230] text-white flex items-center justify-center transition"
              style={{ width: 48 }}
            >
              📷
            </button>
          </div>
          <p className="text-[11px] text-[#8B9690] mt-2 leading-relaxed">
            Powered by Open Food Facts — 3 M+ UK & EU products. Tap 📷 to scan a live barcode <b>or a QR code</b> (meal-kit cards, recipe sites).
          </p>
        </div>
      </div>

      {cameraOpen && (
        <BarcodeCameraModal
          onDetected={(code) => {
            // Guard against zxing firing a second frame after we've already
            // routed. Any second call becomes a no-op.
            if (!cameraOpen) return;
            setCameraOpen(false);
            const decision = routeScan(code);
            if (decision.kind === "barcode") {
              setBarcode(decision.value);
              toast.success(`Barcode detected: ${decision.value}`);
              return;
            }
            if (decision.kind === "recipe_url") {
              toast.success("Recipe QR — importing…");
              navigate(`/app/recipes?import=${encodeURIComponent(decision.url)}`);
              return;
            }
            if (decision.kind === "unknown_url") {
              setUrlChoice({ url: decision.url });
              return;
            }
            toast.error("Couldn't read that code — try again with better lighting.");
          }}
          onClose={() => setCameraOpen(false)}
        />
      )}

      {urlChoice && (
        <UrlChoiceModal
          url={urlChoice.url}
          onImportAsRecipe={() => { const u = urlChoice.url; setUrlChoice(null); navigate(`/app/recipes?import=${encodeURIComponent(u)}`); }}
          onOpen={() => { window.open(urlChoice.url, "_blank", "noopener"); setUrlChoice(null); }}
          onClose={() => setUrlChoice(null)}
        />
      )}

      {/* Supermarket URL — perfect for online shoppers */}
      <div className="mt-4 rounded-3xl border border-[#E5E2DA] bg-white p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs uppercase tracking-wide text-[#8B9690]">Or paste a supermarket URL</span>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#EDF3E9] text-[#3B5A3E] font-medium">Online shopping</span>
        </div>
        <input
          type="url"
          inputMode="url"
          placeholder="https://www.tesco.com/… or asda.com / morrisons.com / sainsburys.co.uk / aldi.co.uk …"
          value={supermarketUrl}
          onChange={(e) => setSupermarketUrl(e.target.value)}
          data-testid="supermarket-url-input"
          className="ff-input w-full text-sm"
          maxLength={800}
        />
        <p className="text-[11px] text-[#8B9690] mt-2 leading-relaxed">
          Copy any product page URL from Tesco, Sainsbury&apos;s, Asda, Morrisons, Waitrose, Ocado, Aldi, Lidl, Iceland, Co-op or M&amp;S — we&apos;ll pull the ingredients + nutrition and give you the verdict.
        </p>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button
          onClick={analyse}
          disabled={analysing}
          data-testid="analyse-btn"
          className="rounded-full bg-[#3B5A3E] hover:bg-[#2A4230] disabled:opacity-60 text-white text-sm font-medium px-6 py-3 transition"
        >
          {analysing ? "Reading label…" : "Analyse this product"}
        </button>
        <button
          onClick={clearAll}
          data-testid="clear-btn"
          className="text-xs text-[#5C6B61] hover:text-[#3B5A3E] transition"
        >
          Clear
        </button>
      </div>

      {/* RESULT PANEL */}
      {analysing && (
        <div className="mt-8 rounded-3xl border border-[#E5E2DA] bg-white p-8 text-center" data-testid="analyse-loading">
          <div className="text-4xl mb-3 animate-pulse">🔍</div>
          <div className="text-[#3B5A3E] font-medium">Reading the label…</div>
          <div className="text-xs text-[#8B9690] mt-1">OCR + nutrition analysis (about 8 seconds)</div>
        </div>
      )}

      {result && <ResultCard result={result} onSave={saveFavourite} saving={savingFav} isPro={isPro} goPro={() => navigate("/app/billing")} offlineHit={offlineHit} />}

      {savedOpen && (
        <SavedScansDrawer
          scans={savedScans}
          onClose={() => setSavedOpen(false)}
          onRemove={(key) => { scanCache.remove(key); setSavedScans(scanCache.list()); }}
          onReopen={(entry) => { setResult(entry.scan); setOfflineHit(null); setSavedOpen(false); window.scrollTo({ top: 0, behavior: "smooth" }); }}
        />
      )}

      {/* FAVOURITES — grouped by category folders */}
      {favourites.length > 0 && (
        <FavouriteFolders
          favourites={favourites}
          onRemove={removeFavourite}
        />
      )}
    </div>
  );
}

// ---------- sub-components ----------

function ImagePicker({ testid, label, hint, value, onFile, onClear, inputRef }) {
  return (
    <div className="rounded-3xl border border-[#E5E2DA] bg-white p-4 flex flex-col">
      <div className="text-xs uppercase tracking-wide text-[#8B9690]">{label}</div>
      <div className="text-sm text-[#5C6B61] mb-3">{hint}</div>
      {value ? (
        <div className="relative">
          <img
            src={value.preview}
            alt=""
            className="w-full rounded-2xl object-cover"
            style={{ maxHeight: 200 }}
          />
          <button
            onClick={onClear}
            data-testid={`${testid}-clear`}
            className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 border border-[#E5E2DA] text-sm shadow-sm hover:bg-white"
            aria-label="Remove image"
          >
            ✕
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          data-testid={`${testid}-btn`}
          className="w-full h-32 rounded-2xl border-2 border-dashed border-[#C7CBB7] hover:border-[#3B5A3E] hover:bg-[#F3F0EA]/50 flex flex-col items-center justify-center gap-1 transition"
        >
          <span className="text-2xl">📷</span>
          <span className="text-xs text-[#5C6B61]">Tap to snap or upload</span>
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onFile}
        data-testid={`${testid}-file`}
      />
    </div>
  );
}

const VERDICT_STYLES = {
  good: { bg: "#D5EBD9", fg: "#1F5030", emoji: "🟢", label: "Good choice" },
  moderate: { bg: "#FBE9C4", fg: "#7A5A11", emoji: "🟡", label: "Moderate — occasional" },
  avoid: { bg: "#F6C7BE", fg: "#7A2317", emoji: "🔴", label: "Best to avoid" },
};

const TL_COLOR = { green: "#3B5A3E", amber: "#C18928", red: "#A33D26" };
const CONCERN_COLOR = { low: "#3B5A3E", medium: "#C18928", high: "#A33D26" };

function ResultCard({ result, onSave, saving, isPro, goPro, offlineHit }) {
  const v = VERDICT_STYLES[result.verdict] || VERDICT_STYLES.moderate;
  const n = result.nutrition_per_100g || {};
  const tl = result.traffic_lights || {};
  const ib = Array.isArray(result.ingredients_breakdown) ? result.ingredients_breakdown : [];
  const hi = Array.isArray(result.health_impact) ? result.health_impact : [];
  const pc = Array.isArray(result.potential_causes) ? result.potential_causes : [];
  const swap = result.healthier_swap || null;
  const isTeaser = !!result.is_teaser;

  return (
    <div className="mt-8 rounded-3xl bg-white border border-[#E5E2DA] overflow-hidden" data-testid="scan-result">
      {offlineHit && (
        <div
          className="px-5 py-3 bg-[#FDE8D7] border-b border-[#F6C7BE] text-[#7A2317] text-xs font-medium flex items-center gap-2"
          data-testid="offline-fallback-banner"
        >
          <span>📡</span>
          <span>Showing your saved result for this barcode {offlineHit.days_old === 0 ? "from today" : `from ${offlineHit.days_old} day${offlineHit.days_old === 1 ? "" : "s"} ago`}. Rescan when you&apos;re back online for the freshest ingredients.</span>
        </div>
      )}
      {result.from_shared_library && !offlineHit && (
        <div
          className="px-5 py-2.5 bg-[#EDF3E9] border-b border-[#D5EBD9] text-[#1F5030] text-[11px] font-medium flex items-center gap-2"
          data-testid="shared-library-banner"
        >
          <span>⚡</span>
          <span>Instant result — another Shop, Cal &amp; Meal user already scanned this. Your allergens and nasties are still checked for you.</span>
        </div>
      )}
      {/* Allergen safety banner — top priority. Rendered even on free teaser. */}
      {result.allergen_match && <AllergenSafetyBanner match={result.allergen_match} />}

      {/* Nasties watchlist hits — only shows if user has flagged something that matched */}
      {result.nasties_match?.hits?.length > 0 && <NastiesBanner match={result.nasties_match} />}

      {/* Verdict banner */}
      <div className="p-5" style={{ background: v.bg, color: v.fg }}>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wide opacity-80">{v.label}</div>
            <div className="text-2xl sm:text-3xl font-semibold mt-1 truncate" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="result-product-name">
              {v.emoji} {result.product_name}
            </div>
            {result.brand && <div className="text-sm opacity-80 mt-0.5">{result.brand}</div>}
            {result.verdict_reason && <div className="text-sm mt-2 leading-relaxed">{result.verdict_reason}</div>}
          </div>
          <button
            onClick={isPro ? onSave : goPro}
            disabled={saving}
            data-testid="save-favourite-btn"
            className="shrink-0 rounded-full bg-white/80 hover:bg-white text-sm font-medium px-4 py-2 border border-white/60 disabled:opacity-60 transition"
            style={{ color: v.fg }}
            title={isPro ? "Save to favourites" : "Pro-only — upgrade to save products"}
          >
            {saving ? "Saving…" : isPro ? "★ Save" : "🔒 Save (Pro)"}
          </button>
        </div>
      </div>

      {/* NOVA + traffic lights strip */}
      <div className="p-5 border-b border-[#E5E2DA] flex flex-wrap gap-3 items-center">
        {result.nova_level && (
          <span
            className="rounded-full px-3 py-1 text-xs font-medium"
            style={{ background: result.nova_level >= 4 ? "#F6C7BE" : result.nova_level >= 3 ? "#FBE9C4" : "#D5EBD9", color: "#2A332C" }}
            data-testid="nova-badge"
          >
            NOVA {result.nova_level} · {result.nova_label || "processing level"}
          </span>
        )}
        {Object.entries(tl).map(([k, val]) => (
          <span
            key={k}
            className="rounded-full px-3 py-1 text-xs font-medium text-white"
            style={{ background: TL_COLOR[val] || "#8B9690" }}
            data-testid={`tl-${k}`}
          >
            {k.replace("_", " ")}: {val}
          </span>
        ))}
      </div>

      {/* "Watch out" chip strip — surfaces concerning chemicals at a glance.
         Shown even on teaser scans because free users get only verdict + traffic
         lights deep-locked below; this one chip summary isn't deep analysis — it's
         "here's the shortlist of nasties this label contains". */}
      {!isTeaser && <WatchOutStrip breakdown={ib} /> }

      {/* Teaser paywall — free tier scans get everything above, deep breakdown locked */}
      {isTeaser && (
        <div className="relative border-b border-[#E5E2DA] overflow-hidden" data-testid="teaser-lock">
          <div className="p-5 select-none" style={{ filter: "blur(6px)", pointerEvents: "none", opacity: 0.7 }} aria-hidden="true">
            <div className="text-xs uppercase tracking-wide text-[#8B9690] mb-2">Ingredient breakdown</div>
            <ul className="space-y-2">
              {[1, 2, 3, 4].map((i) => (
                <li key={i} className="flex items-start gap-3 text-sm">
                  <span className="mt-1 shrink-0 w-2.5 h-2.5 rounded-full bg-[#8B9690]" />
                  <div className="min-w-0">
                    <div className="font-medium">Ingredient placeholder text goes here</div>
                    <div className="text-[#5C6B61] text-xs mt-0.5">One-sentence explanation about the additive and any concerns.</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div className="absolute inset-0 flex items-center justify-center p-5 bg-gradient-to-b from-white/40 to-white/95">
            <div className="max-w-md text-center bg-white rounded-2xl border border-[#E5E2DA] shadow-lg p-6">
              <div className="text-3xl">🔒</div>
              <h3 className="mt-2 text-lg font-semibold" style={{ fontFamily: "Outfit, sans-serif" }}>
                Unlock the full breakdown
              </h3>
              <p className="text-xs text-[#5C6B61] mt-2 leading-relaxed">
                {result.upsell_message || "Pro unlocks the full ingredient analysis, health impact, causes, weight-loss fit and healthier swap."}
              </p>
              <button
                onClick={goPro}
                data-testid="teaser-upgrade-btn"
                className="mt-4 rounded-full bg-[#3B5A3E] hover:bg-[#2A4230] text-white text-sm font-medium px-6 py-3 transition"
              >
                Start 24-hour free trial →
              </button>
              <p className="text-[10px] text-[#8B9690] mt-3">Free plan: 1 scan / week · Pro: unlimited</p>
            </div>
          </div>
        </div>
      )}

      {/* Nutrition per 100g */}
      <div className="p-5 border-b border-[#E5E2DA]">
        <div className="text-xs uppercase tracking-wide text-[#8B9690] mb-2">Nutrition per 100 g</div>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3" data-testid="nutrition-grid">
          <Nutr label="kcal" value={n.kcal} />
          <Nutr label="sugar" unit="g" value={n.sugar_g} />
          <Nutr label="salt" unit="g" value={n.salt_g} />
          <Nutr label="sat fat" unit="g" value={n.sat_fat_g} />
          <Nutr label="fibre" unit="g" value={n.fibre_g} />
          <Nutr label="protein" unit="g" value={n.protein_g} />
        </div>
      </div>

      {/* Ingredients breakdown */}
      {ib.length > 0 && (
        <div className="p-5 border-b border-[#E5E2DA]">
          <div className="text-xs uppercase tracking-wide text-[#8B9690] mb-2">Ingredient breakdown</div>
          <ul className="space-y-2" data-testid="ingredients-breakdown">
            {ib.map((i, idx) => (
              <li key={idx} className="flex items-start gap-3 text-sm">
                <span
                  className="mt-1 shrink-0 w-2.5 h-2.5 rounded-full"
                  style={{ background: CONCERN_COLOR[i.concern] || "#8B9690" }}
                />
                <div className="min-w-0">
                  <div className="font-medium">{i.name} <span className="text-[#8B9690] font-normal">· {i.role}</span></div>
                  {i.note && <div className="text-[#5C6B61] text-xs mt-0.5">{i.note}</div>}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Health impact & causes */}
      {(hi.length > 0 || pc.length > 0) && (
        <div className="p-5 border-b border-[#E5E2DA] grid sm:grid-cols-2 gap-6">
          {hi.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wide text-[#8B9690] mb-2">Health impact</div>
              <ul className="space-y-1.5 text-sm text-[#2A332C]" data-testid="health-impact-list">
                {hi.map((x, i) => <li key={i}>• {x}</li>)}
              </ul>
            </div>
          )}
          {pc.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wide text-[#8B9690] mb-2">May contribute to</div>
              <ul className="space-y-1.5 text-sm text-[#2A332C]" data-testid="potential-causes-list">
                {pc.map((x, i) => <li key={i}>• {x}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Weight loss compat */}
      {result.weight_loss_note && (
        <div className="p-5 border-b border-[#E5E2DA] flex items-start gap-3 bg-[#F3F0EA]/60">
          <span className="text-xl" aria-hidden>⚖️</span>
          <div>
            <div className="text-xs uppercase tracking-wide text-[#8B9690]">Weight-loss fit</div>
            <div className="text-sm mt-1" data-testid="weight-loss-note">
              {result.weight_loss_compatible ? "✓ Compatible — " : "⚠ Watch out — "}
              {result.weight_loss_note}
            </div>
          </div>
        </div>
      )}

      {/* Healthier swap */}
      {swap && (swap.suggestion || swap.why) && (
        <div className="p-5 bg-[#EDF3E9] flex items-start gap-3">
          <span className="text-xl" aria-hidden>🌱</span>
          <div>
            <div className="text-xs uppercase tracking-wide text-[#3B5A3E]">Healthier swap</div>
            <div className="text-sm mt-1 font-medium" data-testid="healthier-swap">{swap.suggestion}</div>
            {swap.why && <div className="text-xs text-[#5C6B61] mt-1">{swap.why}</div>}
          </div>
        </div>
      )}
    </div>
  );
}

function Nutr({ label, value, unit }) {
  const shown = value == null || Number.isNaN(value) ? "—" : Math.round(Number(value) * 10) / 10;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-[#8B9690]">{label}</div>
      <div className="text-lg font-medium">
        {shown}
        {unit && shown !== "—" && <span className="text-xs text-[#8B9690] ml-0.5">{unit}</span>}
      </div>
    </div>
  );
}

function FavouriteCard({ fav, onRemove }) {
  const v = VERDICT_STYLES[fav.verdict] || VERDICT_STYLES.moderate;
  return (
    <div className="rounded-2xl border border-[#E5E2DA] bg-white p-3 flex items-start gap-3" data-testid={`favourite-${fav.favourite_id}`}>
      <div className="w-10 h-10 rounded-full flex items-center justify-center text-lg shrink-0" style={{ background: v.bg }}>
        {v.emoji}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium truncate">{fav.product_name}</div>
        {fav.brand && <div className="text-xs text-[#8B9690] truncate">{fav.brand}</div>}
        <div className="text-[11px] text-[#5C6B61] mt-1">
          {fav.kcal_per_100g != null && <>{Math.round(fav.kcal_per_100g)} kcal/100g · </>}
          {fav.nova_level != null && <>NOVA {fav.nova_level}</>}
        </div>
      </div>
      <button
        onClick={onRemove}
        className="text-xs text-[#A33D26] hover:underline shrink-0"
        data-testid={`remove-favourite-${fav.favourite_id}`}
      >
        Remove
      </button>
    </div>
  );
}


// ---------- Allergen picker (top of the Food Scanner page) ----------
function AllergenPicker({ catalog, selected, onToggle, open, setOpen }) {
  if (!catalog || catalog.length === 0) {
    return null;
  }
  const selectedCount = selected.length;
  // Default-closed, but auto-expand first time the user has nothing set.
  const showAll = open || selectedCount === 0;
  const visible = showAll ? catalog : catalog.filter((a) => selected.includes(a.key));

  return (
    <div
      className="mb-5 rounded-3xl border border-[#E5E2DA] bg-white p-4"
      data-testid="allergen-picker"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wide text-[#8B9690]">Allergy & diet safety</div>
          <div className="text-sm font-medium mt-0.5" style={{ fontFamily: "Outfit, sans-serif" }}>
            {selectedCount > 0
              ? `Flagging ${selectedCount} ingredient${selectedCount === 1 ? "" : "s"} on every scan`
              : "Pick what you can't eat"}
          </div>
          <div className="text-xs text-[#5C6B61] mt-0.5">
            We'll warn you at the top of every scan if it's not safe for you.
          </div>
        </div>
        {selectedCount > 0 && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="text-xs text-[#3B5A3E] font-medium hover:underline shrink-0 whitespace-nowrap"
            data-testid="allergen-picker-toggle"
          >
            {showAll ? "Collapse" : "Edit"}
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {visible.map((a) => {
          const on = selected.includes(a.key);
          return (
            <button
              key={a.key}
              type="button"
              onClick={() => onToggle(a.key)}
              data-testid={`allergen-chip-${a.key}`}
              className={
                "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition " +
                (on
                  ? "bg-[#3B5A3E] text-white border-[#3B5A3E]"
                  : "bg-white text-[#2A332C] border-[#E5E2DA] hover:border-[#3B5A3E]")
              }
            >
              <span aria-hidden>{a.emoji}</span>
              <span>{a.label}</span>
              {on && <span className="ml-0.5" aria-hidden>✓</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------- Allergen safety banner (top of the result card) ----------
const SAFETY_STYLES = {
  safe:            { bg: "#D5EBD9", fg: "#1F5030", emoji: "🟢", label: "Safe for you" },
  unsafe:          { bg: "#F6C7BE", fg: "#7A2317", emoji: "🔴", label: "Not safe for you" },
  unclear:         { bg: "#FBE9C4", fg: "#7A5A11", emoji: "🟡", label: "Check the pack" },
  no_preferences:  { bg: "#F3F0EA", fg: "#5C6B61", emoji: "ℹ️", label: "No preferences set" },
};

function AllergenSafetyBanner({ match }) {
  const style = SAFETY_STYLES[match.safety] || SAFETY_STYLES.no_preferences;
  const chips = [
    ...(match.hits_present || []).map((h) => ({ ...h, kind: "present" })),
    ...(match.hits_unclear || []).map((h) => ({ ...h, kind: "unclear" })),
  ];
  return (
    <div
      className="px-5 py-4 border-b border-[#E5E2DA]"
      style={{ background: style.bg, color: style.fg }}
      data-testid={`allergen-safety-${match.safety}`}
    >
      <div className="flex items-start gap-3">
        <div className="text-2xl shrink-0" aria-hidden>{style.emoji}</div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] uppercase tracking-wide opacity-80">{style.label}</div>
          <div className="text-sm font-medium mt-0.5 leading-relaxed">{match.message}</div>
          {chips.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {chips.map((c) => (
                <span
                  key={`${c.kind}-${c.key}`}
                  className="inline-flex items-center gap-1 rounded-full bg-white/70 px-2 py-0.5 text-[11px]"
                  style={{ color: style.fg }}
                  data-testid={`allergen-hit-${c.kind}-${c.key}`}
                >
                  <span aria-hidden>{c.emoji}</span>
                  <span>{c.label.split(" (")[0]}</span>
                  {c.kind === "unclear" && <span className="opacity-70">may contain</span>}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}



// ---------- "Watch out" chip strip — concerning chemicals at a glance ----------
const CONCERN_CHIP = {
  high:   { bg: "#F6C7BE", fg: "#7A2317", emoji: "🚨" },
  medium: { bg: "#FBE9C4", fg: "#7A5A11", emoji: "⚠️" },
};
function WatchOutStrip({ breakdown }) {
  const nasties = (breakdown || [])
    .filter((it) => it && (it.concern === "high" || it.concern === "medium"))
    .slice(0, 6);
  if (nasties.length === 0) return null;
  return (
    <div className="px-5 py-3 border-b border-[#E5E2DA] bg-[#FBF8F1]" data-testid="watch-out-strip">
      <div className="text-[10px] uppercase tracking-wide text-[#7A5A11] mb-2">⚠️ Watch out — concerning ingredients</div>
      <div className="flex flex-wrap gap-1.5">
        {nasties.map((n, i) => {
          const style = CONCERN_CHIP[n.concern] || CONCERN_CHIP.medium;
          return (
            <span
              key={i}
              className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium"
              style={{ background: style.bg, color: style.fg }}
              data-testid={`watch-out-chip-${i}`}
              title={n.note || ""}
            >
              <span aria-hidden>{style.emoji}</span>
              <span className="truncate max-w-[180px]">{n.name}</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

// ---------- Favourite folders — group saved products by category ----------
const CATEGORY_META = {
  "Snacks & biscuits":  { emoji: "🍪", bg: "#FBE9C4" },
  "Drinks":             { emoji: "🥤", bg: "#CDE4F3" },
  "Breakfast":          { emoji: "🥣", bg: "#F3EBDC" },
  "Bakery & bread":     { emoji: "🍞", bg: "#F6E2C2" },
  "Dairy & eggs":       { emoji: "🥛", bg: "#EFEFEF" },
  "Meat & fish":        { emoji: "🥩", bg: "#F6D4C8" },
  "Fruit & veg":        { emoji: "🥬", bg: "#D5EBD9" },
  "Ready meals":        { emoji: "🍱", bg: "#F3F0EA" },
  "Pantry & cooking":   { emoji: "🫙", bg: "#EDE4D1" },
  "Treats & chocolate": { emoji: "🍫", bg: "#E6CFC2" },
  "Condiments & sauces":{ emoji: "🥫", bg: "#F6DCC2" },
  "Other":              { emoji: "📦", bg: "#F3F0EA" },
};
const CATEGORY_ORDER = Object.keys(CATEGORY_META);

function FavouriteFolders({ favourites, onRemove }) {
  // Group by category — fall back to "Other" for anything the LLM didn't tag.
  const grouped = {};
  for (const f of favourites) {
    const key = CATEGORY_META[f.category] ? f.category : "Other";
    (grouped[key] = grouped[key] || []).push(f);
  }
  const orderedKeys = CATEGORY_ORDER.filter((k) => grouped[k]?.length);
  // Default to the first folder being open.
  const [openCat, setOpenCat] = useState(orderedKeys[0] || null);

  return (
    <section className="mt-10" data-testid="favourites-section">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold" style={{ fontFamily: "Outfit, sans-serif" }}>
          Your saved products <span className="text-[#8B9690] font-normal text-sm">({favourites.length})</span>
        </h2>
      </div>
      <div className="space-y-3">
        {orderedKeys.map((cat) => {
          const meta = CATEGORY_META[cat];
          const items = grouped[cat];
          const isOpen = openCat === cat;
          return (
            <div
              key={cat}
              className="rounded-2xl border border-[#E5E2DA] bg-white overflow-hidden"
              data-testid={`folder-${cat.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              <button
                type="button"
                onClick={() => setOpenCat(isOpen ? null : cat)}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-[#FAF9F6] transition"
                data-testid={`folder-toggle-${cat.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center text-xl shrink-0"
                    style={{ background: meta.bg }}
                  >
                    {meta.emoji}
                  </div>
                  <div className="min-w-0 text-left">
                    <div className="text-sm font-medium truncate" style={{ fontFamily: "Outfit, sans-serif" }}>
                      {cat}
                    </div>
                    <div className="text-xs text-[#8B9690]">
                      {items.length} saved · {items.filter((i) => i.verdict === "good").length} good · {items.filter((i) => i.verdict === "moderate").length} moderate
                    </div>
                  </div>
                </div>
                <span className="text-[#8B9690] text-sm shrink-0">{isOpen ? "▾" : "▸"}</span>
              </button>
              {isOpen && (
                <div className="px-4 pb-4 pt-1 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {items.map((f) => (
                    <FavouriteCard key={f.favourite_id} fav={f} onRemove={() => onRemove(f.favourite_id)} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ---------- Nasties watchlist picker ----------
function NastiesPicker({ library, selected, onToggle, onAddCustom, open, setOpen }) {
  const [custom, setCustom] = useState("");
  const selectedCount = selected.length;
  const showAll = open || selectedCount === 0;
  const librarySet = new Set(library.map((n) => n.label.toLowerCase()));
  const customOnly = selected.filter((s) => !librarySet.has(s.toLowerCase()));

  const handleAdd = (e) => {
    e?.preventDefault?.();
    const t = custom.trim();
    if (!t) return;
    onAddCustom(t);
    setCustom("");
  };

  return (
    <div
      className="mb-5 rounded-3xl border border-[#E5E2DA] bg-white p-4"
      data-testid="nasties-picker"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wide text-[#8B9690]">Chemical / additive watchlist</div>
          <div className="text-sm font-medium mt-0.5" style={{ fontFamily: "Outfit, sans-serif" }}>
            {selectedCount > 0
              ? `Watching for ${selectedCount} ingredient${selectedCount === 1 ? "" : "s"} on every scan`
              : "Flag specific additives you want to avoid"}
          </div>
          <div className="text-xs text-[#5C6B61] mt-0.5">
            E.g. aspartame, titanium dioxide, HFCS, nitrites. We'll warn you if any scan contains them.
          </div>
        </div>
        {selectedCount > 0 && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="text-xs text-[#3B5A3E] font-medium hover:underline shrink-0 whitespace-nowrap"
            data-testid="nasties-picker-toggle"
          >
            {showAll ? "Collapse" : "Edit"}
          </button>
        )}
      </div>

      {showAll && (
        <>
          <div className="flex flex-wrap gap-2 mb-3">
            {library.map((n) => {
              const on = selected.some((s) => s.toLowerCase() === n.label.toLowerCase());
              return (
                <button
                  key={n.label}
                  type="button"
                  onClick={() => onToggle(n.label)}
                  data-testid={`nasty-chip-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={
                    "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition " +
                    (on
                      ? "bg-[#7A2317] text-white border-[#7A2317]"
                      : "bg-white text-[#2A332C] border-[#E5E2DA] hover:border-[#7A2317]")
                  }
                >
                  <span aria-hidden>{on ? "⚠️" : "+"}</span>
                  <span>{n.label}</span>
                  {on && <span className="ml-0.5" aria-hidden>✓</span>}
                </button>
              );
            })}
          </div>
          {customOnly.length > 0 && (
            <div className="mb-3">
              <div className="text-[10px] uppercase tracking-wide text-[#8B9690] mb-1">Your custom entries</div>
              <div className="flex flex-wrap gap-2">
                {customOnly.map((label) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => onToggle(label)}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#7A2317] text-white border border-[#7A2317] px-3 py-1.5 text-xs font-medium"
                    data-testid={`nasty-custom-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    <span aria-hidden>⚠️</span>
                    <span>{label}</span>
                    <span className="opacity-80 ml-1" aria-hidden>✕</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <form onSubmit={handleAdd} className="flex items-center gap-2">
            <input
              type="text"
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              placeholder="+ Add your own (e.g. carrageenan)"
              maxLength={40}
              className="flex-1 min-w-0 rounded-full border border-[#E5E2DA] bg-white px-4 py-2 text-sm focus:outline-none focus:border-[#3B5A3E]"
              data-testid="nasty-custom-input"
            />
            <button
              type="submit"
              disabled={!custom.trim()}
              className="rounded-full bg-[#3B5A3E] text-white text-xs font-medium px-4 py-2 disabled:opacity-40"
              data-testid="nasty-custom-add"
            >
              Add
            </button>
          </form>
        </>
      )}
    </div>
  );
}

// ---------- Nasties banner (shown on scan result only if any hits) ----------
function NastiesBanner({ match }) {
  const hits = match.hits || [];
  const names = hits.map((h) => h.term).join(", ");
  return (
    <div
      className="px-5 py-4 border-b border-[#E5E2DA]"
      style={{ background: "#F6C7BE", color: "#7A2317" }}
      data-testid="nasties-banner"
    >
      <div className="flex items-start gap-3">
        <div className="text-2xl shrink-0" aria-hidden>⚠️</div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] uppercase tracking-wide opacity-80">On your watchlist</div>
          <div className="text-sm font-medium mt-0.5 leading-relaxed">
            Contains {names} — you flagged {hits.length === 1 ? "this" : "these"}.
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {hits.map((h, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1 rounded-full bg-white/70 px-2 py-0.5 text-[11px]"
                style={{ color: "#7A2317" }}
                data-testid={`nasty-hit-${i}`}
              >
                <span aria-hidden>⚠️</span>
                <span>{h.term}</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}


const SAVED_FILTERS = [
  { key: "all", label: "All" },
  { key: "safe", label: "Safe for me" },
  { key: "watch", label: "Watch out" },
  { key: "avoid", label: "Avoid" },
];

function bucketOf(entry) {
  const s = entry.allergen_match?.status;
  if (s === "unsafe" || entry.verdict === "avoid") return "avoid";
  if (s === "warning" || entry.verdict === "moderate" || entry.nasties_match?.hits?.length) return "watch";
  return "safe";
}

function SavedScansDrawer({ scans, onClose, onRemove, onReopen }) {
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  const filtered = scans.filter((e) => {
    if (filter !== "all" && bucketOf(e) !== filter) return false;
    if (!q.trim()) return true;
    const needle = q.trim().toLowerCase();
    return [e.product_name, e.brand, e.barcode].some((s) => (s || "").toLowerCase().includes(needle));
  });

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center sm:justify-center bg-black/40 p-0 sm:p-6" data-testid="saved-scans-drawer">
      <div className="bg-white w-full sm:max-w-2xl sm:rounded-3xl rounded-t-3xl max-h-[85vh] flex flex-col overflow-hidden shadow-xl border border-[#E5E2DA]">
        <div className="px-5 pt-5 pb-3 border-b border-[#E5E2DA] flex items-start gap-3">
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-wide text-[#8B9690]">Offline ready</div>
            <h2 className="text-xl font-semibold mt-0.5" style={{ fontFamily: "Outfit, sans-serif" }}>
              Your saved scans
            </h2>
            <p className="text-[11px] text-[#5C6B61] mt-1">Every item you&apos;ve scanned is kept on this device so you can rescan with no signal.</p>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="close-saved-scans-btn" className="shrink-0 w-9 h-9 rounded-full hover:bg-[#F3F0EA] text-[#5C6B61]">✕</button>
        </div>

        <div className="px-5 py-3 border-b border-[#E5E2DA] space-y-2">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, brand or barcode"
            className="ff-input w-full text-sm"
            data-testid="saved-search-input"
          />
          <div className="flex flex-wrap gap-2" role="tablist">
            {SAVED_FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`saved-filter-${f.key}`}
                className={`text-xs rounded-full px-3 py-1 border transition ${filter === f.key ? "bg-[#3B5A3E] text-white border-[#3B5A3E]" : "bg-white text-[#5C6B61] border-[#E5E2DA] hover:border-[#3B5A3E]"}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-2" data-testid="saved-list">
          {filtered.length === 0 ? (
            <div className="text-center py-10 text-sm text-[#8B9690]">
              {scans.length === 0
                ? "No saved scans yet. Every scan you run will land here automatically."
                : "Nothing matches that filter."}
            </div>
          ) : (
            filtered.map((e) => {
              const bucket = bucketOf(e);
              const chip = bucket === "safe" ? { bg: "#D5EBD9", fg: "#1F5030", label: "Safe" } : bucket === "watch" ? { bg: "#FBE9C4", fg: "#7A5A11", label: "Watch out" } : { bg: "#F6C7BE", fg: "#7A2317", label: "Avoid" };
              return (
                <div key={e.key} className="rounded-2xl border border-[#E5E2DA] p-3 flex items-center gap-3" data-testid={`saved-item-${e.key}`}>
                  {e.image_url ? (
                    <img src={e.image_url} alt="" className="w-12 h-12 rounded-xl object-cover shrink-0 border border-[#E5E2DA]" />
                  ) : (
                    <div className="w-12 h-12 rounded-xl bg-[#F3F0EA] shrink-0 flex items-center justify-center text-xl">🛒</div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{e.product_name}</div>
                    <div className="text-[11px] text-[#8B9690] truncate">
                      {[e.brand, e.barcode].filter(Boolean).join(" · ") || "No brand"}
                    </div>
                  </div>
                  <span className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium" style={{ background: chip.bg, color: chip.fg }}>
                    {chip.label}
                  </span>
                  <button onClick={() => onReopen(e)} className="shrink-0 text-xs font-medium text-[#3B5A3E] hover:underline" data-testid={`reopen-saved-${e.key}`}>Open</button>
                  <button onClick={() => onRemove(e.key)} className="shrink-0 w-8 h-8 rounded-full hover:bg-[#F3F0EA] text-[#8B9690]" title="Remove" aria-label="Remove" data-testid={`remove-saved-${e.key}`}>✕</button>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}


function UrlChoiceModal({ url, onImportAsRecipe, onOpen, onClose }) {
  const short = url.length > 60 ? url.slice(0, 57) + "…" : url;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4" data-testid="qr-url-choice-modal" onClick={onClose}>
      <div className="bg-white rounded-3xl max-w-sm w-full p-5 shadow-xl border border-[#E5E2DA]" onClick={(e) => e.stopPropagation()}>
        <div className="text-[10px] uppercase tracking-wide text-[#8B9690]">QR code</div>
        <h3 className="text-lg font-semibold mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>What should we do with this link?</h3>
        <div className="mt-2 text-xs text-[#5C6B61] break-all">{short}</div>
        <div className="mt-5 space-y-2">
          <button onClick={onImportAsRecipe} className="w-full rounded-full bg-[#3B5A3E] text-white text-sm py-2.5 font-medium hover:bg-[#2F4932]" data-testid="qr-import-recipe-btn">Try to import as a recipe</button>
          <button onClick={onOpen} className="w-full rounded-full border border-[#E5E2DA] text-sm py-2.5 hover:border-[#3B5A3E]" data-testid="qr-open-link-btn">Open the link in a new tab</button>
          <button onClick={onClose} className="w-full rounded-full text-sm py-2 text-[#5C6B61] hover:bg-[#F3F0EA]" data-testid="qr-cancel-btn">Cancel</button>
        </div>
      </div>
    </div>
  );
}

